import { AssignmentTurnedIn, Delete } from '@mui/icons-material';
import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { calculateFundRateCost } from 'lib/functions/fundCostRateCalculation';
import React from 'react';
import { useDispatch } from 'react-redux';

import CustomButton from '../../../../../../../../../lib/designSystem/Button';
import { cellFormat } from '../../../../../../../../../lib/designSystem/Table/cellFormat';
import { LightTooltip } from '../../../../../../../../../lib/designSystem/WhiteTooltip';
import {
  calculateDays,
  isDateAWeekDay,
} from '../../../../../../../../../lib/functions/dates';
import {
  formatDate,
  formatMoney,
  showAlert,
} from '../../../../../../../../../lib/functions/utils';

const useStyles = makeStyles(() =>
  createStyles({
    table: {
      width: '100%',
      minWidth: '1400px',
      borderCollapse: 'separate',
      borderSpacing: 0,
    },
    tableCellSmall: {
      padding: '12px 8px !important',
      overflow: 'hidden',
      minWidth: '20px',
      maxWidth: '300px',
      whiteSpace: 'nowrap',
      fontSize: '13px !important',
      borderBottom: '1px solid #f0f0f0',
      verticalAlign: 'middle',
    },
    tableCell: {
      padding: '12px 8px !important',
      overflow: 'hidden',
      minWidth: '80px',
      maxWidth: '150px',
      whiteSpace: 'nowrap',
      fontSize: '13px !important',
      borderBottom: '1px solid #f0f0f0',
      verticalAlign: 'middle',
    },
    tableCellTasa: {
      padding: '12px 8px !important',
      overflow: 'hidden',
      minWidth: '80px',
      maxWidth: '100px',
      whiteSpace: 'nowrap',
      fontSize: '13px !important',
      textAlign: 'right',
      borderBottom: '1px solid #f0f0f0',
      verticalAlign: 'middle',
    },
    tableCellNames: {
      padding: '12px 8px !important',
      overflow: 'hidden',
      minWidth: '100px',
      maxWidth: '120px',
      whiteSpace: 'nowrap',
      fontSize: '13px !important',
      borderBottom: '1px solid #f0f0f0',
      verticalAlign: 'middle',
    },
    tableContainer: {
      border: '1px solid #e8e8e8',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      overflow: 'auto',
      maxHeight: '600px',
      overflowY: 'auto',
      overflowX: 'auto',
    },
    tableBody: {
      backgroundColor: '#ffffff',
    },
    tableHead: {
      backgroundColor: '#f8f9fa !important',
      '& th': {
        backgroundColor: '#f8f9fa !important',
        color: '#495057 !important',
        fontWeight: '600 !important',
        fontSize: '13px !important',
        padding: '16px 8px !important',
        borderBottom: '2px solid #dee2e6',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      },
    },
    textField: {
      fontSize: '13px !important',
      '& .MuiOutlinedInput-root': {
        borderRadius: '6px',
        '& fieldset': {
          borderColor: '#e0e0e0',
        },
        '&:hover fieldset': {
          borderColor: '#1976d2',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#1976d2',
        },
      },
      '& .MuiInputBase-input': {
        padding: '8px 12px',
        fontSize: '13px',
      },
    },
    textFieldAutocomplete: {
      fontSize: '13px !important',
      minWidth: '120px !important',
      '& .MuiOutlinedInput-root': {
        borderRadius: '6px',
        '& fieldset': {
          borderColor: '#e0e0e0',
        },
        '&:hover fieldset': {
          borderColor: '#1976d2',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#1976d2',
        },
      },
      '& .MuiInputBase-input': {
        padding: '6px 12px',
        fontSize: '13px',
      },
    },
    iconButton: {
      padding: '6px',
      margin: '0 2px',
      borderRadius: '6px',
      '&:hover': {
        backgroundColor: 'rgba(25, 118, 210, 0.08)',
      },
    },
    deleteButton: {
      color: '#dc3545',
      '&:hover': {
        backgroundColor: 'rgba(220, 53, 69, 0.08)',
      },
    },
    assignButton: {
      color: '#28a745',
      '&:hover': {
        backgroundColor: 'rgba(40, 167, 69, 0.08)',
      },
    },
    tableRow: {
      '&:hover': {
        backgroundColor: '#f8f9fa',
        transition: 'background-color 0.2s ease',
      },
      '&:nth-of-type(even)': {
        backgroundColor: '#fafbfc',
      },
      '&:nth-of-type(even):hover': {
        backgroundColor: '#f1f3f4',
      },
    },
    amountCell: {
      fontWeight: '600',
      color: '#2c3e50',
    },
    daysCell: {
      fontWeight: '500',
      color: '#6c757d',
    },
    rateCostCell: {
      fontWeight: '600',
      color: '#28a745',
    },
    actionsCell: {
      minWidth: '80px',
      maxWidth: '100px',
      padding: '8px !important',
    },
    chip: {
      fontSize: '11px',
      height: '20px',
      borderRadius: '10px',
    },
    '@media (max-width: 1200px)': {
      tableCell: {
        fontSize: '12px !important',
        padding: '8px 4px !important',
        maxWidth: '150px',
      },
      tableCellNames: {
        fontSize: '12px !important',
        padding: '8px 4px !important',
        maxWidth: '100px',
      },
      tableCellTasa: {
        fontSize: '12px !important',
        padding: '8px 4px !important',
        maxWidth: '60px',
      },
    },
  }),
);

const ResellsTable = (props: any) => {
  const {
    resells,
    setResells,
    funds,
    canForcePaymentDate,
    readOnly = false,
    markResellAsModified,
    isNomineeView = false,
    setOpenDocumentSelector,
    setOpenDocumentBatchImporter,
  } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const fundCalculationMethodHash = funds.reduce(
    (acc: Record<string, string | undefined>, fund: any) => {
      const calculationMethod =
        fund.FundCompanyRelation?.[0]?.calculationMethod || 'SIMPLE';

      if (fund.id) {
        acc[fund.id] = calculationMethod;
      }
      return acc;
    },
    {} as Record<string, string | undefined>,
  );

  const dateValidators = (date: Date, type: string, rowIndex: number) => {
    if (canForcePaymentDate) {
      const days = calculateDays(
        type === 'startDate' ? date : new Date(resells[rowIndex].startDate),
        type === 'endDate' ? date : new Date(resells[rowIndex].endDate),
      );
      if (days <= 0) {
        showAlert(
          dispatch,
          'warning',
          'La fecha de vencimiento no puede ser menor a la fecha de liberación',
        );
        returnTempDateToActualDate(rowIndex);
        return false;
      }
      return days;
    }
    if (!isDateAWeekDay(date) && type === 'endDate') {
      showAlert(
        dispatch,
        'warning',
        'La fecha de vencimiento seleccionada no es un día hábil.',
      );
    }
    if (date < new Date() && type === 'endDate') {
      showAlert(
        dispatch,
        'warning',
        'La fecha de vencimiento no puede ser menor a la fecha actual',
      );
      returnTempDateToActualDate(rowIndex);
      return false;
    }
    const days = calculateDays(
      type === 'startDate' ? date : new Date(resells[rowIndex].startDate),
      type === 'endDate' ? date : new Date(resells[rowIndex].endDate),
    );
    if (days <= 0) {
      showAlert(
        dispatch,
        'warning',
        'La fecha de vencimiento no puede ser menor a la fecha de liberación',
      );
      returnTempDateToActualDate(rowIndex);
      return false;
    }
    return days;
  };

  const returnTempDateToActualDate = (rowIndex: number) => {
    const newResells = resells.map((doc: any, i: any) => {
      if (i === rowIndex) {
        return {
          ...doc,
          tempEndDate: doc.endDate,
        };
      }
      return doc;
    });
    setResells(newResells);
  };

  const handleChangeTempDate = (e: any, type: string, rowIndex: number) => {
    const newResells = resells.map((doc: any, i: any) => {
      if (i === rowIndex) {
        return {
          ...doc,
          [type]: e.target.value,
        };
      }
      return doc;
    });
    setResells(newResells);
  };

  const handleChangeDate = (value: any, type: string, rowIndex: number) => {
    const days = dateValidators(new Date(value), type, rowIndex);
    if (!days) return;
    const resell = resells[rowIndex];
    const newResells = resells.map((doc: any, i: any) => {
      if (i === rowIndex) {
        const rateCost = calculateFundRateCost(
          doc.amount,
          parseFloat(doc.advance),
          parseFloat(resell.operationCostFixed),
          parseFloat(resell.rate),
          days,
          fundCalculationMethodHash,
          doc.fund?.id,
        );
        return {
          ...doc,
          [type]: value,
          days,
          rateCost,
        };
      }
      return doc;
    });
    setResells(newResells);
    if (markResellAsModified && resell.id) {
      markResellAsModified(resell.id);
    }
  };

  const handleChangeCessionDate = (value: any, rowIndex: number) => {
    const resell = resells[rowIndex];
    const newResells = resells.map((doc: any, i: any) => {
      if (i === rowIndex) {
        return {
          ...doc,
          cessionDate: value,
        };
      }
      return doc;
    });
    setResells(newResells);
    if (markResellAsModified && resell.id) {
      markResellAsModified(resell.id);
    }
  };

  const getRate = (value: any) => {
    if (value === '') {
      return parseFloat('0');
    }
    if (value.endsWith('.')) {
      return parseFloat(value);
    }
    return parseFloat(value);
  };

  const handleChangeCalculatedValue = (
    e: any,
    rowIndex: number,
    type: string,
  ) => {
    let valueToCalculate = 0;
    let value = e.target.value;
    if (type === 'amount') {
      value = value.split('.').join('').split('$').join('');
      if (value === '') {
        valueToCalculate = parseFloat('0');
      } else if (isNaN(parseFloat(value))) {
        return;
      } else {
        valueToCalculate = parseFloat(value);
      }
      if (valueToCalculate > resells[rowIndex].originalAmount)
        return showAlert(
          dispatch,
          'warning',
          'El monto a financiar no puede ser mayor al monto del documento',
        );
    } else {
      const isValidFormat = /^\d*\.?\d*$/.test(value);
      if (!isValidFormat) return;
      valueToCalculate = getRate(value);
      if (valueToCalculate > 100 || valueToCalculate < 0) return;
    }
    const newResells = resells.map((doc: any, i: any) => {
      if (i === rowIndex) {
        const rateCost = calculateFundRateCost(
          type === 'amount' ? valueToCalculate : parseFloat(doc.amount),
          type === 'advance' ? valueToCalculate : parseFloat(doc.advance),
          parseFloat(doc.operationCostFixed),
          type === 'rate' ? valueToCalculate : parseFloat(doc.rate),
          doc.days,
          fundCalculationMethodHash,
          doc.fund?.id,
        );

        return {
          ...doc,
          rateCost,
          amount: type === 'amount' ? value : parseFloat(doc.amount),
          rate: type === 'rate' ? value : parseFloat(doc.rate),
          advance: type === 'advance' ? value : parseFloat(doc.advance),
          arrearRate:
            type === 'arrearRate' ? value : parseFloat(doc.arrearRate),
        };
      }
      return doc;
    });
    setResells(newResells);
    if (markResellAsModified && resells[rowIndex].id) {
      markResellAsModified(resells[rowIndex].id);
    }
  };

  const getDisplayValue = (rate: any) => {
    if (!rate || rate === '') {
      return '0';
    }
    return rate.toString();
  };

  const handleChangeOperationCostFixed = (e: any, rowIndex: number) => {
    const value = e.target.value.split('.').join('').split('$').join('');
    if (isNaN(value)) return;
    if (value < 0) return;
    const newResells = resells.map((doc: any, i: any) => {
      if (i === rowIndex) {
        const rateCost = calculateFundRateCost(
          doc.amount,
          parseFloat(doc.advance),
          parseFloat(value),
          parseFloat(doc.rate),
          doc.days,
          fundCalculationMethodHash,
          doc.fund?.id,
        );
        return {
          ...doc,
          operationCostFixed: value,
          rateCost,
        };
      }
      return doc;
    });
    setResells(newResells);
    if (markResellAsModified && resells[rowIndex].id) {
      markResellAsModified(resells[rowIndex].id);
    }
  };

  const assignConditionsForAllResells = (sourceRowIndex: number) => {
    const newResells = resells.map((doc: any, i: any) => {
      // Skip rows above the source row (keep them unchanged)
      if (i <= sourceRowIndex) {
        return doc;
      }
      // Copy conditions from source row to all rows below
      const rateCost = calculateFundRateCost(
        doc.amount,
        parseFloat(resells[sourceRowIndex].advance),
        parseFloat(resells[sourceRowIndex].operationCostFixed),
        parseFloat(resells[sourceRowIndex].rate),
        resells[sourceRowIndex].days,
        fundCalculationMethodHash,
        resells[sourceRowIndex].fund?.id,
      );
      return {
        ...doc,
        rate: resells[sourceRowIndex].rate,
        rateCost,
        arrearsRate: resells[sourceRowIndex].arrearsRate,
        arrearTableId: resells[sourceRowIndex].arrearTableId,
        advance: resells[sourceRowIndex].advance,
        startDate: resells[sourceRowIndex].startDate,
        endDate: resells[sourceRowIndex].endDate,
        tempEndDate: resells[sourceRowIndex].endDate,
        operationCostFixed: resells[sourceRowIndex].operationCostFixed,
        operationCostVariable: resells[sourceRowIndex].operationCostVariable,
        fundId: resells[sourceRowIndex].fundId,
        fund: resells[sourceRowIndex].fund,
        days: resells[sourceRowIndex].days,
      };
    });
    setResells([
      ...newResells.map((resell: any, index: any) => ({
        ...resell,
        key: index,
      })),
    ]); // Ensure a new array reference with unique keys
  };

  const assignRateToAllResells = () => {
    if (resells.length === 0) return;

    const firstRate = resells[0].rate;
    const newResells = resells.map((doc: any, i: any) => {
      if (i === 0) {
        return doc;
      }
      const rateCost = calculateFundRateCost(
        doc.amount,
        parseFloat(doc.advance),
        parseFloat(doc.operationCostFixed),
        parseFloat(firstRate),
        doc.days,
        fundCalculationMethodHash,
        doc.fund?.id,
      );
      return {
        ...doc,
        rate: firstRate,
        rateCost,
      };
    });

    setResells([
      ...newResells.map((resell: any, index: any) => ({
        ...resell,
        key: index,
      })),
    ]);
  };

  const deleteRow = (rowIndex: number) => {
    const newInvoices = resells.map((doc: any, i: any) => {
      if (i === rowIndex) {
        return {
          ...doc,
          deleted: true,
        };
      }
      return doc;
    });
    if (markResellAsModified && resells[rowIndex].id) {
      markResellAsModified(resells[rowIndex].id);
    }
    setResells(newInvoices);
  };

  return (
    <>
      <div
        style={{
          padding: '10px 0px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">Facturas</Typography>
        {!isNomineeView && (
          <div style={{ display: 'flex', gap: 10 }}>
            <CustomButton
              onClick={() => setOpenDocumentBatchImporter(true)}
              color="secondary"
            >
              Importar
            </CustomButton>
            <CustomButton
              onClick={() => setOpenDocumentSelector(true)}
              color="primary"
            >
              Agregar
            </CustomButton>
          </div>
        )}
      </div>
      <TableContainer className={classes.tableContainer}>
        <Table className={classes.table}>
          <TableHead className={classes.tableHead}>
            <TableRow>
              {!readOnly && (
                <TableCell className={classes.actionsCell}></TableCell>
              )}
              <TableCell className={classes.tableCell}>Folio</TableCell>
              <TableCell className={classes.tableCellNames}>Cliente</TableCell>
              <TableCell className={classes.tableCellNames}>Deudor</TableCell>
              <TableCell className={classes.tableCell}>Monto Doc.</TableCell>
              <TableCell className={classes.tableCell}>Tipo</TableCell>
              <TableCell className={classes.tableCell}>Solicitud</TableCell>
              <TableCell className={classes.tableCell}>Liberación</TableCell>
              <TableCell className={classes.tableCell}>Monto</TableCell>
              <TableCell className={classes.tableCell}>Anticipo</TableCell>
              <TableCell className={classes.tableCellTasa}>
                <LightTooltip
                  placement="top"
                  title={
                    <Typography style={{ padding: 8 }}>
                      Hacer clic para asignar la tasa del primer elemento a
                      todos
                    </Typography>
                  }
                >
                  <Box
                    onClick={assignRateToAllResells}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      '&:hover': {
                        color: '#1976d2',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Tasa (%)
                  </Box>
                </LightTooltip>
              </TableCell>
              <TableCell className={classes.tableCell}>Tasa mora(%)</TableCell>
              <TableCell className={classes.tableCell}>Comisión</TableCell>
              <TableCell className={classes.tableCell}>
                Fecha cesión obtenida
              </TableCell>
              <TableCell className={classes.tableCell}>
                Fecha vencimiento
              </TableCell>
              <TableCell className={classes.tableCell}>
                Monto financiado
              </TableCell>
              <TableCell className={classes.tableCell}>Días</TableCell>
              <TableCell className={classes.tableCell}>
                Diferencia precio
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody className={classes.tableBody}>
            {resells.map((row: any, rowIndex: number) => {
              return (
                <TableRow key={rowIndex} className={classes.tableRow}>
                  {!readOnly && (
                    <TableCell className={classes.actionsCell}>
                      <Box display="flex" justifyContent="start">
                        <IconButton
                          className={classes.iconButton}
                          onClick={() => deleteRow(rowIndex)}
                        >
                          <Delete className={classes.deleteButton} />
                        </IconButton>
                        <LightTooltip
                          placement="right"
                          title={
                            <Typography style={{ padding: 12 }}>
                              Asignar mismas condiciones a todas las filas de
                              abajo
                            </Typography>
                          }
                        >
                          <IconButton
                            className={classes.iconButton}
                            onClick={() =>
                              assignConditionsForAllResells(rowIndex)
                            }
                          >
                            <AssignmentTurnedIn
                              className={classes.assignButton}
                            />
                          </IconButton>
                        </LightTooltip>
                      </Box>
                    </TableCell>
                  )}
                  <TableCell className={classes.tableCell}>
                    {row.folio}
                  </TableCell>
                  <TableCell className={classes.tableCellNames}>
                    {row.businessName}
                  </TableCell>
                  <TableCell className={classes.tableCellNames}>
                    {row.stakeholderName}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Box className={classes.amountCell} textAlign="right">
                      <Chip
                        label={formatMoney(row.originalAmount)}
                        className={classes.chip}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  </TableCell>
                  <TableCell className={classes.tableCellSmall}>
                    {row.requestType ? row.requestType : 'N/A'}
                  </TableCell>
                  <TableCell className={classes.tableCellTasa}>
                    {row.requestId
                      ? cellFormat(row.requestId, 'numeric-id')
                      : 'N/A'}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatDate(row.paymentDate)}
                  </TableCell>
                  <TableCell className={classes.tableCellTasa}>
                    <TextField
                      value={formatMoney(row.amount)}
                      onChange={(e) =>
                        handleChangeCalculatedValue(e, rowIndex, 'amount')
                      }
                      onFocus={(e) => {
                        if (e.target.value === '0') {
                          e.target.select();
                        }
                      }}
                      InputProps={{ classes: { input: classes.textField } }}
                      disabled={readOnly}
                    />
                  </TableCell>
                  <TableCell className={classes.tableCellTasa}>
                    <TextField
                      value={getDisplayValue(row.advance)}
                      onChange={(e) =>
                        handleChangeCalculatedValue(e, rowIndex, 'advance')
                      }
                      onFocus={(e) => {
                        if (e.target.value === '0') {
                          e.target.select();
                        }
                      }}
                      InputProps={{ classes: { input: classes.textField } }}
                      disabled={readOnly}
                    />
                  </TableCell>
                  <TableCell className={classes.tableCellTasa}>
                    <TextField
                      value={getDisplayValue(row.rate)}
                      onChange={(e) =>
                        handleChangeCalculatedValue(e, rowIndex, 'rate')
                      }
                      onFocus={(e) => {
                        if (e.target.value === '0') {
                          e.target.select();
                        }
                      }}
                      InputProps={{ classes: { input: classes.textField } }}
                      disabled={readOnly}
                    />
                  </TableCell>
                  <TableCell className={classes.tableCellTasa}>
                    <TextField
                      value={getDisplayValue(row.arrearRate)}
                      onChange={(e) =>
                        handleChangeCalculatedValue(e, rowIndex, 'arrearRate')
                      }
                      onFocus={(e) => {
                        if (e.target.value === '0') {
                          e.target.select();
                        }
                      }}
                      InputProps={{ classes: { input: classes.textField } }}
                      disabled={readOnly}
                    />
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <TextField
                      value={formatMoney(row.operationCostFixed)}
                      onChange={(e) =>
                        handleChangeOperationCostFixed(e, rowIndex)
                      }
                      InputProps={{ classes: { input: classes.textField } }}
                      disabled={readOnly}
                    />
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <TextField
                      value={row.cessionDate}
                      type="date"
                      onChange={(e) =>
                        handleChangeCessionDate(e.target.value, rowIndex)
                      }
                      InputProps={{ classes: { input: classes.textField } }}
                      disabled={readOnly}
                    />
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <TextField
                      value={row.tempEndDate}
                      type="date"
                      onChange={(e) =>
                        handleChangeTempDate(e, 'tempEndDate', rowIndex)
                      }
                      onBlur={() =>
                        handleChangeDate(
                          resells[rowIndex].tempEndDate,
                          'endDate',
                          rowIndex,
                        )
                      }
                      InputProps={{ classes: { input: classes.textField } }}
                      disabled={readOnly}
                    />
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Box className={classes.daysCell}>
                      <Chip
                        label={`${formatMoney((row.amount * row.advance) / 100) || 0}`}
                        className={classes.chip}
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Box className={classes.daysCell}>
                      <Chip
                        label={`${row.days || 0} días`}
                        className={classes.chip}
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Box className={classes.rateCostCell} textAlign="right">
                      <Chip
                        label={formatMoney(row.rateCost)}
                        className={classes.chip}
                        color="success"
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default ResellsTable;
